import { Spin, Table, Tabs, Tooltip } from 'antd';
import { orderBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  getCRSModelExplainabilityData,
  getCRSModelExplainabilityStats
} from '../../../../redux/actions/crs/crsRiskDetail';
import {
  CRS_CONSTANTS,
  CRS_EXPLAINABILITY_TAB_ORDER
} from '../../../constants/crsContants';
import Card from '../../components/Card';
import styles from './index.scss';
import { getItemFromLocalStorage } from '../../../../util/localStorageUtils';
const { TabPane } = Tabs;

const { GET_DATA } = CRS_CONSTANTS;
const INCONSISTENCY_BEHAVIOUR_CATEGORY = 'INCONSISTENCY BEHAVIOUR';

const HISTORICAL_BEHAVIOUR = 'HISTORICAL BEHAVIOUR';

const columns = [
  {
    title: 'Rule Name',
    dataIndex: 'featureName'
  },
  {
    title: 'Value',
    dataIndex: 'featureValue'
  },
  {
    title: 'Contribution %',
    dataIndex: 'featureContribution',
    render: (contribution) => {
      const formatted = Math.round(contribution * 10000) / 100 + '%';
      return (
        <div className={styles.featureContribution}>
          <Tooltip placement="leftBottom" title={contribution}>
            <strong>{formatted}</strong>
          </Tooltip>
        </div>
      );
    }
  }
];

// columns for inconsistency behavior
const inconsistencyColumns = [
  {
    title: 'Rule Name',
    dataIndex: 'featureName'
  },
  {
    title: 'Threshold',
    dataIndex: 'featureThreshold',

    render: (threshold) => {
      return threshold ? Number(threshold).toFixed(2) : '-';
    }
  },
  {
    title: 'Cluster Mean',
    dataIndex: 'featureClusterMean',

    render: (clusterMean) => {
      return clusterMean ? Number(clusterMean).toFixed(2) : '-';
    }
  },
  {
    title: 'Value',
    dataIndex: 'featureValue',

    render: (featureValue) => {
      return !Number.isNaN(featureValue)
        ? Number(featureValue).toFixed(2)
        : featureValue;
    }
  },
  {
    title: 'Contribution %',
    dataIndex: 'featureContribution',
    render: (contribution) => {
      const formatted = Math.round(contribution * 10000) / 100 + '%';
      return (
        <div className={styles.featureContribution}>
          <Tooltip placement="leftBottom" title={contribution}>
            <strong>{formatted}</strong>
          </Tooltip>
        </div>
      );
    }
  }
];

const returnCustomColumns = (category) => {
  if (category === INCONSISTENCY_BEHAVIOUR_CATEGORY) {
    return inconsistencyColumns;
  } else {
    return columns;
  }
};

const tabOrderComparator = (category) =>
  CRS_EXPLAINABILITY_TAB_ORDER[category.explainabilityCategory];

const RiskTag = ({ riskLevel }) => {
  switch (riskLevel) {
    case 'High':
      return (
        <div className={styles.highTag}>
          <span>High</span>
        </div>
      );
    case 'Medium':
    case 'Mid':
      return (
        <div className={styles.midTag}>
          <span>Mid</span>
        </div>
      );
    case 'Low':
      return (
        <div className={styles.lowTag}>
          <span>Low</span>
        </div>
      );
    default:
      return <></>;
  }
};

const tabsToHide = ['INCONSISTENCY BEHAVIOUR', 'HISTORICAL BEHAVIOUR'];

export const ScoreExplainability = ({
  customerKey,
  GSConfig,
  modelExplainAbilityResponse: {
    isFetching: isDataFetching,
    fetched: dataFetched,
    data
  },
  modelExplainAbilityStatsResponse: {
    isFetching: isStatsFetching,
    fetched: statsFetched,
    stats
  },
  dispatch,
  isPrioritizedAlert,
  alertDetails
}) => {
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const scoreExplainabilityTabs =
    stats?.filter(
      (item) => !tabsToHide.includes(item.explainabilityCategory)
    ) || [];
  const sortedStats = orderBy(
    scoreExplainabilityTabs,
    [tabOrderComparator],
    ['asc']
  );

  const fetchStats = () =>
    dispatch(
      getCRSModelExplainabilityStats(
        {
          customerKey,
          alertId: isPrioritizedAlert ? alertDetails?.data?.alertId : '-1'
        },
        GET_DATA
      )
    );

  const fetchCategoryString = (category) => {
    switch (category) {
      case INCONSISTENCY_BEHAVIOUR_CATEGORY:
        return 'INCONSISTENCY_BEHAVIOUR';
      case HISTORICAL_BEHAVIOUR:
        return 'HISTORICAL_BEHAVIOUR';
      default:
        return category;
    }
  };

  const fetchData = (page, pageSize) => {
    dispatch(
      getCRSModelExplainabilityData(
        {
          customerKey,

          category: fetchCategoryString(
            sortedStats[activeTabKey - 1].explainabilityCategory
          ),

          limit: pageSize,
          offset: page * pageSize,
          alertId: isPrioritizedAlert ? alertDetails?.data?.alertId : '-1'
        },
        GET_DATA
      )
    );
  };

  const onTabChange = (key) => {
    const page = 1;
    setActiveTabKey(key);
    setCurrentPage(page);
  };

  const onPaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
    fetchData(page - 1, pageSize);
  };

  // call the API to get categories, when page initially loads
  useEffect(() => {
    fetchStats();
  }, []);

  // call the API to get data, whenever we have categories or tab changes.
  useEffect(() => {
    if (statsFetched && stats.length > 0) fetchData(currentPage - 1, pageSize);
  }, [stats, activeTabKey]);

  const RulesTable = () => (
    <Table
      className={styles.table}
      dataSource={data?.crsFeatureContributions}
      columns={returnCustomColumns(
        sortedStats[activeTabKey - 1].explainabilityCategory
      )}
      loading={isDataFetching}
      rowKey="featureName"
      pagination={{
        total: data?.count || 0,
        current: currentPage,
        pageSize,
        onChange: onPaginationChange,
        showTotal: (total) => `${total} Records`,
        showSizeChanger: false
      }}
    />
  );

  const getTabName = (category) => {
    const scoreExplainabilityTabs =
      getItemFromLocalStorage('score-explainability-tabs') &&
      JSON.parse(getItemFromLocalStorage('score-explainability-tabs'));

    if (scoreExplainabilityTabs && scoreExplainabilityTabs[category]) {
      return scoreExplainabilityTabs[category];
    }

    return category;
  };

  // hide if isVisible is set to false in global settings
  if (GSConfig?.isVisible === false) return null;

  return (
    <Card header="Score Explainability">
      {isStatsFetching ? (
        <Spin />
      ) : (
        <Tabs
          className={styles.tabs}
          activeKey={activeTabKey}
          onChange={onTabChange}>
          {sortedStats.map((stat, idx) => (
            <TabPane
              tab={
                <div className={styles.tabWithTag}>
                  <span className={styles.tabName}>
                    {/* {formatTabName(stat.explainabilityCategory)} */}
                    {getTabName(stat.explainabilityCategory?.toLowerCase())}
                  </span>
                  <div
                    className={styles.tabNameTag}
                    style={{
                      color: stat.riskLevelColor,
                      border: `1px solid ${stat.riskLevelColor}8f`,
                      backgroundColor: `${stat.riskLevelColor}14`
                    }}>
                    <span>{stat.riskLevelLabel || stat.riskLevel}</span>
                  </div>
                </div>
              }
              key={`${idx + 1}`}>
              <RulesTable />
            </TabPane>
          ))}
        </Tabs>
      )}
    </Card>
  );
};

RiskTag.propTypes = {
  riskLevel: PropTypes.number
};

ScoreExplainability.propTypes = {
  GSConfig: PropTypes.object,
  customerKey: PropTypes.string,
  modelExplainAbilityResponse: PropTypes.object,
  modelExplainAbilityStatsResponse: PropTypes.object,
  dispatch: PropTypes.func,
  isPrioritizedAlert: PropTypes.bool,
  alertDetails: PropTypes.object
};

function mapStateToProps(state, ownProps = {}) {
  const { modelExplainAbilityResponse, modelExplainAbilityStatsResponse } =
    state.crsRiskDetailReducer;

  return {
    modelExplainAbilityResponse: modelExplainAbilityResponse || {},
    modelExplainAbilityStatsResponse: modelExplainAbilityStatsResponse || {}
  };
}

function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ScoreExplainability);
